import { GRID_COLS } from '@/features/dashboard/lib/grid-layout-config'
import { projectSizeConstraints, type SizeConstraints } from '@/features/dashboard/lib/grid-math'

export type ComponentKey =
  | 'account-cards'
  | 'chat-order-form'
  | 'ticker'
  | 'economic-calendar'
  | 'price-chart'
  | 'watchlist'
  | 'positions'
  | 'risk-slides'
  | 'trades'
  | 'order-book'
  | 'newsfeed'
  | 'data'
  | 'preset-manager'
  | 'quick-order'
  | 'one-tap-buy'
  | 'spreader'

export interface ComponentDefaultSize {
  w: number
  h: number
  minW: number
  minH: number
  maxW?: number
  maxH?: number
}

export interface ComponentMobileSize {
  heightDvh: number
}

export const WIDGET_MOBILE_SIZES: Record<ComponentKey, ComponentMobileSize> = {
  'price-chart': { heightDvh: 0.55 },
  'order-book': { heightDvh: 0.5 },
  trades: { heightDvh: 0.4 },
  ticker: { heightDvh: 0.45 },
  watchlist: { heightDvh: 0.5 },
  positions: { heightDvh: 0.4 },
  'account-cards': { heightDvh: 0.35 },
  'economic-calendar': { heightDvh: 0.45 },
  newsfeed: { heightDvh: 0.5 },
  'risk-slides': { heightDvh: 0.55 },
  data: { heightDvh: 0.45 },
  'preset-manager': { heightDvh: 0.3 },
  'chat-order-form': { heightDvh: 0 },
  'quick-order': { heightDvh: 0.55 },
  'one-tap-buy': { heightDvh: 0.3 },
  spreader: { heightDvh: 0.55 },
}

export const WIDGET_DEFAULT_SIZES: Record<ComponentKey, ComponentDefaultSize> = {
  'chat-order-form': { w: 8, h: 16, minW: 7, minH: 8, maxW: 14, maxH: 24 },
  'account-cards': { w: 6, h: 6, minW: 5, minH: 4 },
  ticker: { w: 8, h: 10, minW: 6, minH: 4 },
  'price-chart': { w: 10, h: 8, minW: 6, minH: 6 },
  watchlist: { w: 5, h: 12, minW: 5, minH: 8 },
  positions: { w: 16, h: 6, minW: 12, minH: 4 },
  'risk-slides': { w: 20, h: 18, minW: 10, minH: 10 },
  trades: { w: 10, h: 8, minW: 4, minH: 6, maxH: 16 },
  'order-book': { w: 10, h: 8, minW: 6, minH: 8 },
  'economic-calendar': { w: 8, h: 8, minW: 6, minH: 6 },
  newsfeed: { w: 6, h: 12, minW: 4, minH: 6 },
  data: { w: 16, h: 6, minW: 8, minH: 6 },
  'preset-manager': { w: 4, h: 5, minW: 3, minH: 2 },
  'quick-order': { w: 8, h: 16, minW: 6, minH: 12, maxW: 12, maxH: 24 },
  'one-tap-buy': { w: 6, h: 6, minW: 6, minH: 4, maxH: 12 },
  spreader: { w: 20, h: 14, minW: 10, minH: 8 },
}

/**
 * Flat minW reduction from each breakpoint's normally-projected value, for widgets
 * that need to pack narrower than the standard studio->laptop->compact ratio allows.
 * `compact` is intentionally absent — those widgets keep the standard ratio there.
 */
const MIN_WIDTH_BREAKPOINT_OVERRIDES: Partial<
  Record<ComponentKey, Partial<Record<'studio' | 'laptop', number>>>
> = {
  'order-book': { studio: 4, laptop: 4 },
  trades: { studio: 2, laptop: 3 },
}

/**
 * Resolve a widget's size constraints directly in `targetCols` coordinate space.
 * `targetCols` must be the FINAL column count the layout will render at — never
 * chain this with projectSizeConstraints again (e.g. re-projecting a studio-scale
 * override down to laptop would silently discard the laptop override).
 */
export function resolveWidgetConstraints(
  key: ComponentKey,
  targetCols: number
): SizeConstraints | undefined {
  const base = WIDGET_DEFAULT_SIZES[key]
  if (!base) return undefined

  const projected: SizeConstraints =
    targetCols === GRID_COLS.studio
      ? base
      : projectSizeConstraints(base, GRID_COLS.studio, targetCols)

  const tier =
    targetCols === GRID_COLS.studio
      ? 'studio'
      : targetCols === GRID_COLS.laptop
        ? 'laptop'
        : undefined
  const minWOverride = tier ? MIN_WIDTH_BREAKPOINT_OVERRIDES[key]?.[tier] : undefined

  return minWOverride === undefined ? projected : { ...projected, minW: Math.max(1, minWOverride) }
}
